/**
 * 통화 상세. GET /api/calls/:id. transcript + summary 같이 노출.
 * SPEC §4: "녹음·STT·요약·재생". 재생 액션은 Kotlin shell의 playAudio(presignedUrl)로.
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { type CallDetail as CallDetailDTO, type SpeakerSegment } from '../lib/api';
import { getCallFromRoom } from '../lib/roomData';
import {
  callStatusLabel,
  categoryLabel,
  durationLabel,
  fullWhen,
  phoneOrFallback,
} from '../lib/format';

const HOST_SPEAKER = '2'; // mocked transcript에서 본인(대표님)은 speaker '2'

export function CallDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['call', id],
    queryFn: () => getCallFromRoom(id),
    enabled: !!id,
    retry: false,
  });

  return (
    <>
      <header className="px-xl pt-3xl pb-lg">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-[12px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors mb-md"
        >
          ← 뒤로
        </button>
        {isLoading && <p className="text-[13px] text-[var(--text-muted)]">로딩 중…</p>}
        {isError && (
          <p className="text-[13px] text-[var(--error)]">
            불러오기 실패: {error instanceof Error ? error.message : String(error)}
          </p>
        )}
        {data && (
          <>
            <h1 className="font-display text-[22px] font-medium text-[var(--text-primary)] leading-tight tracking-tight">
              {phoneOrFallback(data.call.contact_name, data.call.contact_phone)}
            </h1>
            <div className="text-[12px] text-[var(--text-muted)] mt-xs flex items-center gap-sm flex-wrap">
              <span className="font-mono">{fullWhen(data.call.started_at)}</span>
              <span>·</span>
              <span className="font-mono">{durationLabel(data.call.duration_seconds)}</span>
              {categoryLabel(data.call.category) && (
                <span className="px-sm py-[2px] rounded-full bg-[var(--surface-elev)] text-[var(--text-secondary)] border border-[var(--border)] text-[10px]">
                  {categoryLabel(data.call.category)}
                </span>
              )}
            </div>
            {data.call.status !== 'ready' && (
              <p className="mt-md text-[12px] text-[var(--accent)] bg-[var(--accent-tint)] border border-[var(--border)] rounded-md px-md py-sm">
                {callStatusLabel(data.call.status)}{data.call.error ? ` — ${data.call.error}` : ''}
              </p>
            )}
          </>
        )}
      </header>

      {data && (
        <main className="px-xl space-y-md">
          {/* 요약 카드 */}
          <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md p-lg">
            <h2 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--accent)] mb-sm">
              요약
            </h2>
            {data.summary ? (
              <SummaryView summary={data.summary} />
            ) : (
              <p className="text-[13px] text-[var(--text-muted)]">분석 중…</p>
            )}
          </section>

          {/* 화자분리 전사 */}
          <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md p-lg">
            <h2 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-md">
              전사
            </h2>
            {data.transcript ? (
              <TranscriptView segments={data.transcript.speakerSegments} />
            ) : (
              <p className="text-[13px] text-[var(--text-muted)]">분석 중…</p>
            )}
          </section>
        </main>
      )}
    </>
  );
}

function SummaryView({ summary }: { summary: NonNullable<CallDetailDTO['summary']> }) {
  return (
    <div className="space-y-md">
      {summary.topic && (
        <p className="text-[14px] text-[var(--text-primary)] leading-relaxed">{summary.topic}</p>
      )}
      {summary.bullets && summary.bullets.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-xs">주요 내용</div>
          <ul className="space-y-xs text-[13px] text-[var(--text-secondary)] leading-relaxed">
            {summary.bullets.map((b, i) => (
              <li key={i} className="flex gap-sm">
                <span className="text-[var(--text-muted)]">·</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {summary.followups && summary.followups.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-xs">후속 조치</div>
          <ul className="space-y-xs text-[13px] text-[var(--text-secondary)] leading-relaxed">
            {summary.followups.map((b, i) => (
              <li key={i} className="flex gap-sm">
                <span className="text-[var(--accent)]">→</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TranscriptView({ segments }: { segments: SpeakerSegment[] }) {
  if (!segments || segments.length === 0) {
    return <p className="text-[13px] text-[var(--text-muted)]">분석 중…</p>;
  }
  return (
    <ol className="space-y-md">
      {segments.map((seg, i) => {
        const isHost = seg.speaker === HOST_SPEAKER;
        return (
          <li key={i} className="flex gap-md">
            <div
              className={
                'shrink-0 w-7 h-7 rounded-full grid place-items-center text-[11px] font-mono border ' +
                (isHost
                  ? 'bg-[var(--accent-tint)] text-[var(--accent)] border-[var(--accent)]/30'
                  : 'bg-[var(--surface-elev)] text-[var(--text-secondary)] border-[var(--border)]')
              }
              aria-label={isHost ? '대표님' : `상대 ${seg.speaker}`}
            >
              {seg.speaker}
            </div>
            <p
              className={
                'text-[13px] leading-relaxed ' +
                (isHost ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]')
              }
            >
              {seg.text}
            </p>
          </li>
        );
      })}
    </ol>
  );
}
